import { useMemo } from "react";
import { AlarmClock, CalendarDays, Eye, Inbox, Layers, Star } from "lucide-react";
import type { TaskWithFlags } from "@desk/contracts";
import type { NavItem, SidebarAreaNode, SidebarModel, SidebarProjectNode, SidebarSpaceNode } from "@/concepts/types";
import { useHierarchy, useInboxCount, useReview, useTaskList, useToday } from "@/data/hooks";
import { useStored } from "@/lib/storage";
import { href, useLocation, type Route } from "@/router";
import { isClosed } from "@/lib/filters";
import { NAME_MAX, useContainerOps } from "@/features/containers/ContainerActions";

/** Open/total counts per project, for Things-style pie progress glyphs. */
export function useProjectProgress(): Map<string, { done: number; total: number }> {
  const { tasks } = useTaskList({ includeClosed: true, includeSnoozed: true, limit: 500, sort: "created" });
  return useMemo(() => {
    const m = new Map<string, { done: number; total: number }>();
    tasks.forEach((t: TaskWithFlags) => {
      if (!t.projectId || t.deletedAt || t.status === "cancelled") return;
      const e = m.get(t.projectId) ?? { done: 0, total: 0 };
      e.total += 1;
      if (isClosed(t)) e.done += 1;
      m.set(t.projectId, e);
    });
    return m;
  }, [tasks]);
}

/** Open (not done/cancelled) task counts per space (including its projects) and per project. */
export function useOpenCounts(): Map<string, number> {
  const { tasks } = useTaskList({ includeClosed: true, includeSnoozed: true, limit: 500, sort: "created" });
  return useMemo(() => {
    const m = new Map<string, number>();
    const bump = (id: string | null) => id && m.set(id, (m.get(id) ?? 0) + 1);
    tasks.forEach((t: TaskWithFlags) => {
      if (t.deletedAt || isClosed(t)) return;
      bump(t.spaceId);
      bump(t.projectId);
    });
    return m;
  }, [tasks]);
}

export function useSidebarModel(): SidebarModel {
  const h = useHierarchy();
  const loc = useLocation();
  const { view } = useToday(h.today, h.timezone);
  const review = useReview();
  const inbox = useInboxCount();
  const [collapsedAreas, setCollapsedAreas] = useStored<string[]>("sidebar.collapsedAreas", []);
  const [collapsedSpaces, setCollapsedSpaces] = useStored<string[]>("sidebar.collapsedSpaces", []);
  const [settledExpanded, setSettledExpanded] = useStored("sidebar.settledExpanded", false);
  const progress = useProjectProgress();
  const openCounts = useOpenCounts();
  const ops = useContainerOps();
  const rename = (target: Parameters<typeof ops.rename>[0]) => ({
    onRename: (name: string) => void ops.rename(target, name),
    maxLength: NAME_MAX[target.kind],
  });

  const r = loc.route;
  const is = (route: Route) => href(route) === href(r);

  const todayCount = (view?.planned.length ?? 0) + (view?.due.filter((d) => !d.task.flags.isSnoozed).length ?? 0);
  const reviewCount = review.data?.items.filter((i) => !i.task.flags.isSnoozed).length ?? 0;

  const nav: NavItem[] = [
    { id: "today", label: "Today", icon: Star, href: href({ name: "today" }), active: r.name === "today", count: todayCount || undefined },
    { id: "inbox", label: "Inbox", icon: Inbox, href: href({ name: "inbox" }), active: r.name === "inbox", count: inbox || undefined },
    { id: "upcoming", label: "Upcoming", icon: CalendarDays, href: href({ name: "upcoming" }), active: r.name === "upcoming" },
    { id: "review", label: "Review", icon: Eye, href: href({ name: "review" }), active: r.name === "review", count: reviewCount || undefined },
    { id: "snoozed", label: "Snoozed", icon: AlarmClock, href: href({ name: "snoozed" }), active: r.name === "snoozed" },
    { id: "all", label: "All tasks", icon: Layers, href: href({ name: "all" }), active: r.name === "all" },
  ];

  const settled: (SidebarSpaceNode | SidebarProjectNode)[] = [];
  const projectNode = (p: (typeof h.projects)[number]): SidebarProjectNode => ({
    kind: "project",
    project: p,
    href: href({ name: "project", id: p.id }),
    active: is({ name: "project", id: p.id }),
    resting: h.isResting(p),
    snoozedUntil: p.snoozedUntil && p.snoozedUntil > new Date().toISOString() ? p.snoozedUntil : null,
    progress: progress.get(p.id) ?? { done: 0, total: 0 },
    openCount: openCounts.get(p.id) ?? 0,
    rename: rename({ kind: "project", project: p }),
  });

  /** Space nodes for a list of spaces; resting ones (and resting projects) go to Settled. */
  const spaceNodes = (list: typeof h.spaces): SidebarSpaceNode[] => {
    const spaces: SidebarSpaceNode[] = [];
    for (const s of list) {
      const projects: SidebarProjectNode[] = [];
      for (const p of h.projectsOf(s.id)) {
        // Completed projects are settled (G19), so they rest in the Settled section.
        const node = projectNode(p);
        if (node.resting) settled.push(node);
        else projects.push(node);
      }
      const spaceActive = is({ name: "space", id: s.id });
      const node: SidebarSpaceNode = {
        kind: "space",
        space: s,
        href: href({ name: "space", id: s.id }),
        active: spaceActive,
        resting: h.isResting(s),
        snoozedUntil: s.snoozedUntil && s.snoozedUntil > new Date().toISOString() ? s.snoozedUntil : null,
        expanded: !collapsedSpaces.includes(s.id) || projects.some((p) => p.active),
        onToggle: () =>
          setCollapsedSpaces((xs) => (xs.includes(s.id) ? xs.filter((x) => x !== s.id) : [...xs, s.id])),
        projects,
        openCount: openCounts.get(s.id) ?? 0,
        rename: rename({ kind: "space", space: s }),
      };
      if (node.resting) settled.push(node);
      else spaces.push(node);
    }
    return spaces;
  };

  const topSpaces = spaceNodes(h.topSpaces);
  const areas: SidebarAreaNode[] = h.areas.map((a) => {
    const spaces = spaceNodes(h.spacesOf(a.id));
    return {
      area: a,
      href: href({ name: "area", id: a.id }),
      active: is({ name: "area", id: a.id }),
      expanded: !collapsedAreas.includes(a.id),
      onToggle: () => setCollapsedAreas((xs) => (xs.includes(a.id) ? xs.filter((x) => x !== a.id) : [...xs, a.id])),
      spaces,
      rename: rename({ kind: "area", area: a }),
    };
  });

  return {
    nav,
    topSpaces,
    areas,
    settled,
    settledExpanded,
    onToggleSettled: () => setSettledExpanded(!settledExpanded),
  };
}
